import React from 'react';

import { FieldArray, useFormikContext } from 'formik';

import { Typography } from 'homer-web-app-components';

import classNames from 'classnames';

import { LANGUAGES, LANGUAGES_NAMES } from '../../../../../constants/languages';
import {
  PION_TYPES_ICONS,
  PION_TYPES_NAMES,
} from '../../../../../constants/tasks';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  MutateLegend,
  MutateTitle,
  Text,
  Tile,
  TileHeader,
  TileMain,
} from '../../../../Common';
import Errors from '../../../Errors';
import PushTask from '../../../PushTask';
import { PION_CONTENTS, pionHasErrors } from '../MutatePion';

import MutateCardAlterTitle from './MutateCardAlterTitle';
import MutateCardImage from './MutateCardImage';
import MutateCardLegend from './MutateCardLegend';
import MutateCardTags from './MutateCardTags';
import MutateCardTitle from './MutateCardTitle';
import useMutateCardPions from './hooks/useMutateCardPions';

const IMAGE_MIME_TYPES = {
  PNG: 'image/png',
  JPG: 'image/jpg',
  JPEG: 'image/jpeg',
  BMP: 'image/bmp',
  TIFF: 'image/tiff',
  WEBP: 'image/webp',
};

const MutateCard = () => {
  const { values, errors, setFieldValue, setFieldTouched } = useFormikContext();
  const { addPion, removePion } = useMutateCardPions();
  const getPionContent = (type, language, name) =>
    PION_CONTENTS[type]({ language, name });

  return (
    <>
      {Object.keys(errors).length > 0 && <Errors errors={errors} />}
      <Tile theme={Tile.THEME.WHITE}>
        <TileHeader
          style={{ marginBottom: '4rem', display: 'flex' }} // @todo Move `marginBottom` to an util css
        >
          <Typography
            component="h2"
            variant={Typography.THEME.VARIANTS.TITLE_HUGE}
          >
            Entry
          </Typography>
          {values.id && <PushTask />}
        </TileHeader>
        <TileMain style={{ padding: 0 }}>
          {Object.values(LANGUAGES).map((language) => (
            <React.Fragment key={language}>
              <MutateCardLegend
                className={classNames({
                  'mutate-card-legend-error':
                    !values.data.title[language] ||
                    (values.data.title[language] && errors['.title']),
                })}
              >
                {LANGUAGES_NAMES[language]}
              </MutateCardLegend>
              <MutateCardTitle
                style={{ marginBottom: '.5rem' }}
                name={`data.title.${language}`}
                data-testid={`data.title.${language}`}
              />
              <MutateCardAlterTitle
                style={{ marginBottom: '.875rem' }}
                name={`data.alterTitle.${language}`}
                data-testid={`data.alterTitle.${language}`}
              />
              <MutateCardTags
                style={{ marginBottom: '.875rem' }}
                name={`data.tags.${language}`}
                data-testid={`data.tags.${language}`}
                language={language}
              />
              <MutateCardImage
                style={{ marginBottom: '1rem' }}
                language={language}
                type={Object.values(IMAGE_MIME_TYPES)}
                name={`data.image.${language}`}
                data-testid={`data.image.${language}`}
                onSubmit={(value) => {
                  Object.values(LANGUAGES)
                    .filter((l) => l !== language)
                    .forEach((l) => {
                      if (!values.data.image[l]) {
                        setFieldValue(`data.image.${l}`, value);
                        setFieldTouched(`data.image.${l}`, true);
                      }
                    });
                }}
              />
            </React.Fragment>
          ))}
        </TileMain>
      </Tile>
      <Tile theme={Tile.THEME.GIN}>
        <TileHeader style={{ display: 'flex', marginBottom: '4rem' }}>
          <Typography
            component="h2"
            variant={Typography.THEME.VARIANTS.TITLE_HUGE}
          >
            Pions
          </Typography>
        </TileHeader>
        <FieldArray name="data.pions">
          {(fieldArrayHelpers) => (
            <>
              {values.data.pions &&
                values.data.pions.length > 0 &&
                values.data.pions.map((pion, index) => (
                  <TileMain
                    className="pion-block"
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                  >
                    {Object.values(LANGUAGES).map((language, i) => (
                      <React.Fragment key={language}>
                        {i === 0 && (
                          <div style={{ display: 'flex' }}>
                            <Text icon={PION_TYPES_ICONS[pion.type]}>
                              {PION_TYPES_NAMES[pion.type]}
                            </Text>
                            <Button
                              style={{
                                alignSelf: 'center',
                                marginLeft: 'auto',
                              }}
                              theme={Button.THEME.FLAMINGO}
                              sm
                              type="button"
                              onClick={() => {
                                removePion(fieldArrayHelpers, index);
                              }}
                              data-testid="remove-pion"
                            >
                              Remove pion
                            </Button>
                          </div>
                        )}
                        <MutateLegend
                          className={classNames({
                            'mutate-pion-legend-error': pionHasErrors(
                              errors,
                              language,
                              values.data,
                            ),
                          })}
                        >
                          {LANGUAGES_NAMES[language]}
                        </MutateLegend>
                        <MutateTitle
                          style={{ marginBottom: '.25rem' }}
                          name={`data.pions[${index}].payload.title.${language}`}
                          data-testid={`data.pions[${index}].payload.title.${language}`}
                        />
                        {getPionContent(
                          pion.type,
                          language,
                          `data.pions[${index}].payload`,
                        )}
                      </React.Fragment>
                    ))}
                  </TileMain>
                ))}
              <TileMain>
                <Dropdown direction="left">
                  <DropdownToggle
                    // @todo Move `textAlign` to an util css
                    style={{ textAlign: 'center' }}
                    block
                    lg
                    data-testid="add-pion"
                  >
                    Add Pion
                  </DropdownToggle>
                  <DropdownMenu
                    // If dropdown direction is right spacing should be `.5rem` here
                    style={{ marginLeft: '.5rem' }}
                  >
                    {Object.keys(PION_TYPES_NAMES).map((PION_TYPE) => (
                      <DropdownItem
                        key={PION_TYPE}
                        onClick={() => {
                          addPion(fieldArrayHelpers, PION_TYPE);
                        }}
                        data-testid={`add-${PION_TYPE}`}
                      >
                        <Text icon={PION_TYPES_ICONS[PION_TYPE]}>
                          {PION_TYPES_NAMES[PION_TYPE]}
                        </Text>
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              </TileMain>
            </>
          )}
        </FieldArray>
      </Tile>
    </>
  );
};

export default MutateCard;
